import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { library } from '../lib/api';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../i18n';
import { Sidenote, SidenoteLayout } from '../components/ui';

type ReaderTheme = 'paper' | 'sepia' | 'ink';
type ReaderSize  = 'sm' | 'md' | 'lg';

const SIZE_KEY = 'bq.reader.size';
const THEME_KEY = 'bq.reader.theme';

/**
 * ReaderPage — Readwise/Aesop-grade reading environment
 *
 * Features:
 *   - 65ch max-width body
 *   - 3-step font size toggle (15 / 17 / 19px)
 *   - 3 themes (paper / sepia / ink)
 *   - 4-color highlight markers (yellow concept / coral counter / blue definition / mint action)
 *   - localStorage persistence
 *
 * 실제 본문 fetch는 backend Reader endpoint 미구현 — 현재는 preview.overview를 demo chapter로 표시.
 */
export default function ReaderPage() {
  const { t } = useTranslation();
  const { bookId = '' } = useParams();
  const state = useApi(() => library.preview(bookId), [bookId]);

  const [size, setSize] = useState<ReaderSize>(() =>
    (typeof window !== 'undefined' && (localStorage.getItem(SIZE_KEY) as ReaderSize | null)) || 'md',
  );
  const [theme, setTheme] = useState<ReaderTheme>(() =>
    (typeof window !== 'undefined' && (localStorage.getItem(THEME_KEY) as ReaderTheme | null)) || 'paper',
  );

  useEffect(() => { localStorage.setItem(SIZE_KEY, size); }, [size]);
  useEffect(() => { localStorage.setItem(THEME_KEY, theme); }, [theme]);

  if (state.status === 'loading') {
    return <div className="container"><p className="muted">{t('common.loading')}</p></div>;
  }
  if (state.status === 'error') {
    return (
      <div className="container">
        <p className="muted">{t('detail.bookMissing')}</p>
        <Link to="/library">{t('common.back')}</Link>
      </div>
    );
  }

  const book = state.data.book;
  const excerpt = (state.data.map_excerpt as { overview?: string }) ?? {};
  if (!book) {
    return <div className="container"><p className="muted">{t('detail.bookMissing')}</p></div>;
  }

  return (
    <div className={`reader reader--${theme} reader--size-${size}`}>
      <header className="reader__bar">
        <Link to={`/books/${bookId}`} className="reader__crumb">
          ← {book.title}
        </Link>
        <div className="reader__controls" role="toolbar" aria-label={t('reader.controls')}>
          <fieldset className="reader__group">
            <legend className="reader__group-label">{t('reader.size')}</legend>
            {(['sm','md','lg'] as ReaderSize[]).map((s) => (
              <button
                key={s}
                type="button"
                className={`reader__pill${size === s ? ' is-active' : ''}`}
                onClick={() => setSize(s)}
                aria-pressed={size === s}
              >
                {s === 'sm' ? 'A' : s === 'md' ? 'A' : 'A'}
                <span className="visually-hidden">{s}</span>
              </button>
            ))}
          </fieldset>
          <fieldset className="reader__group">
            <legend className="reader__group-label">{t('reader.theme')}</legend>
            {(['paper','sepia','ink'] as ReaderTheme[]).map((th) => (
              <button
                key={th}
                type="button"
                className={`reader__swatch reader__swatch--${th}${theme === th ? ' is-active' : ''}`}
                onClick={() => setTheme(th)}
                aria-pressed={theme === th}
                aria-label={th}
              />
            ))}
          </fieldset>
        </div>
      </header>

      <main className="reader__page">
        <SidenoteLayout>
          <SidenoteLayout.Main>
            <article className="reader__article">
              <div className="reader__kicker">
                {t(`source.${book.source}` as const)} {t('detail.metaSeparator')} {book.language}
              </div>
              <h1 className="reader__title">{book.title}</h1>
              <p className="reader__byline"><em>{t('detail.byAuthor')} {book.author ?? '—'}</em></p>

              <div className="reader__body">
                <p>
                  {excerpt.overview ?? t('reader.previewFallback')}
                </p>
                <p>
                  <mark className="bq-hl bq-hl--yellow">{t('reader.demoHighlightYellow')}</mark>{' '}
                  {t('reader.demoBody1')}{' '}
                  <mark className="bq-hl bq-hl--coral">{t('reader.demoHighlightCoral')}</mark>.
                </p>
                <p>
                  {t('reader.demoBody2')}{' '}
                  <mark className="bq-hl bq-hl--blue">{t('reader.demoHighlightBlue')}</mark>{' '}
                  {t('reader.demoBody3')}{' '}
                  <mark className="bq-hl bq-hl--mint">{t('reader.demoHighlightMint')}</mark>.
                </p>
                <p className="reader__note">— {t('reader.stubNotice')}</p>
              </div>

              <div className="reader__cta-row">
                <Link to={`/books/${bookId}/quiz`} className="btn btn-accent">
                  {t('common.beginReading')} <span className="arrow">→</span>
                </Link>
                <Link to={`/books/${bookId}/map`} className="btn btn-secondary">
                  {t('common.openMindmap')}
                </Link>
              </div>
            </article>
          </SidenoteLayout.Main>

          <SidenoteLayout.Aside>
            <Sidenote label={t('reader.aside.legendLabel')} marker="·">
              <p><span className="reader__hl-dot reader__hl-dot--yellow" /> {t('reader.aside.legendYellow')}</p>
              <p><span className="reader__hl-dot reader__hl-dot--coral" /> {t('reader.aside.legendCoral')}</p>
              <p><span className="reader__hl-dot reader__hl-dot--blue" /> {t('reader.aside.legendBlue')}</p>
              <p><span className="reader__hl-dot reader__hl-dot--mint" /> {t('reader.aside.legendMint')}</p>
            </Sidenote>

            <Sidenote label={t('reader.aside.metaLabel')} tone="mute">
              <p className="mono">{book.page_count} {t('detail.pages')}</p>
              <p className="mono">{book.language}</p>
            </Sidenote>
          </SidenoteLayout.Aside>
        </SidenoteLayout>
      </main>
    </div>
  );
}
